import React, { useEffect, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { CircularProgress } from "@mui/material";
import editIcon from "../../../assets/Report/edit.jpg";
import styles from "./EditableReportSection.module.css";

const EditableReportSection = ({
  title,
  value = "",
  originalValue = "",
  onSave,
  titleClassName = "",
  contentClassName = "",
  showColorBar = true,
  showTitle = true,
  displayAsList = false,
  listClassName = "",
  renderValue,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setDraft(value);
    }
  }, [value, isEditing]);

  const handleEdit = () => {
    setDraft(value);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraft(value);
    setIsEditing(false);
  };

  const handleConfirm = async () => {
    try {
      setIsSaving(true);
      await onSave?.(draft);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save section:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={`${styles.wrapper} ${!showTitle ? styles.wrapper_embedded : ""}`}
    >
      <div
        className={`${styles.header} ${!showTitle ? styles.header_embedded : ""} ${!showColorBar ? styles.header_no_bar : ""}`}
      >
        {showTitle && (
          <div className={styles.title_block}>
            <h2 className={titleClassName || styles.title}>{title}</h2>
            {showColorBar && (
              <div className={styles.color_bar}>
                <span className={styles.bar_orange} />
                <span className={styles.bar_blue} />
              </div>
            )}
          </div>
        )}

        {!isEditing && (
          <button
            type="button"
            className={styles.edit_btn}
            onClick={handleEdit}
            aria-label={`Edit ${title || "section"}`}
            data-ghg-report-edit-ui
          >
            <img src={editIcon} alt="" className={styles.edit_icon} />
          </button>
        )}
      </div>

      {isEditing ? (
        <div className={styles.edit_box}>
          <textarea
            className={styles.edit_textarea}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={8}
            disabled={isSaving}
            data-ghg-report-edit-ui
          />
          <div className={styles.edit_footer} data-ghg-report-edit-ui>
            <div className={styles.action_group}>
              <button
                type="button"
                className={styles.cancel_btn}
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirm_btn}
                onClick={handleConfirm}
                disabled={isSaving}
                aria-label="Save changes"
              >
                {isSaving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CheckIcon className={styles.confirm_icon} />
                )}
              </button>
            </div>
          </div>
        </div>
      ) : renderValue ? (
        renderValue(value)
      ) : displayAsList ? (
        <ul className={`${styles.list} ${listClassName}`.trim()}>
          {value
            .split("\n")
            .map((item) => item.trim())
            .filter(Boolean)
            .map((item, index) => (
              <li key={`${item}-${index}`}>{item}</li>
            ))}
        </ul>
      ) : (
        <p className={`${styles.content} ${contentClassName}`}>{value}</p>
      )}
    </div>
  );
};

export default EditableReportSection;
